/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  OverlayTrigger,
  Popover,
  PopoverBody,
  Form,
} from 'react-bootstrap';
import type {
  Question,
  QuestionGroup,
  QuestionSection,
  ScoringRule,
} from '../../types/types';

import GroupManagerPanel from './GroupManagerPanel';
import QuestionForm from './QuestionForm';
import ChoiceOptionsEditor from './ChoiceOptionsEditor';
import ScoringRulesEditor from './ScoringRulesEditor';
import GroupSelector from './GroupSelector';
import { supabase } from '../../lib/supabase';

interface Props {
  question: Question;
  section: QuestionSection;
  templateId: string;
  existingRule?: ScoringRule | null;
  onSaveQuestion: (q: Question) => void;
  onSaveScoringRule: (rule: ScoringRule) => Promise<void>;
  onClose: () => void;
  onSaveQuestionGroups: (
    sectionId: string,
    groups: QuestionGroup[]
  ) => Promise<void>;
}

const QuestionEditorModal: React.FC<Props> = ({
  question,
  section,
  templateId,
  existingRule,
  onSaveQuestion,
  onSaveScoringRule,
  onClose,
  onSaveQuestionGroups,
}) => {
  const [draft, setDraft] = useState<any>({
    ...question,
    group_id: question.group_id || null,
  });

  const [logic, setLogic] = useState<Record<string, any>>(
    existingRule?.scoring_logic || {}
  );

  const [showGroups, setShowGroups] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<QuestionGroup[]>([]);

  // 🔹 Fetch groups for this section
  useEffect(() => {
    const fetchGroups = async () => {
      const { data, error } = await supabase
        .from('question_groups')
        .select('*')
        .eq('section_id', section.id)
        .order('sort_order', { ascending: true });

      if (!error) setAvailableGroups(data ?? []);
    };

    fetchGroups();
  }, [section.id]);

  // 🔹 Ensure draft.group_id is always valid
  const cleanDraftGroupId = (groupsList: QuestionGroup[]) => {
    const validGroupIds = groupsList.map((g) => g.id);
    if (!validGroupIds.includes(draft.group_id)) {
      setDraft((prev: any) => ({ ...prev, group_id: null }));
    }
  };

  // 🔹 Handle deletion inside availableGroups
  const handleGroupsChange = (newGroups: QuestionGroup[]) => {
    setAvailableGroups(newGroups);

    // If current draft.group_id was deleted, clear it
    cleanDraftGroupId(newGroups);
  };

  const handleSave = async () => {
    // 1️⃣ Save groups first
    await onSaveQuestionGroups(section.id, availableGroups);

    // 2️⃣ Ensure draft.group_id is still valid
    cleanDraftGroupId(availableGroups);

    // 3️⃣ Save question
    onSaveQuestion({ ...draft, group_id: draft.group_id || null });

    // 4️⃣ Save scoring rule
    const scoringRule = {
      id: existingRule?.id,
      question_id: draft.id!,
      template_id: templateId,
      section_id: section.id!,
      scoring_logic: logic,
      weight: draft.weight,
    };

    await onSaveScoringRule(scoringRule as unknown as ScoringRule);

    // 5️⃣ Close modal
    onClose();
  };

  return (
    <Modal show size="lg" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Question - {section?.title}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ position: 'relative', overflow: 'hidden' }}>
        <QuestionForm draft={draft} setDraft={setDraft} section={section} />

        <ChoiceOptionsEditor
          draft={draft}
          setDraft={setDraft}
          logic={logic}
          setLogic={setLogic}
        />

        <ScoringRulesEditor draft={draft} logic={logic} setLogic={setLogic} />

        <GroupSelector
          draft={draft}
          setDraft={setDraft}
          availableGroups={availableGroups}
          onManage={() => setShowGroups(true)}
        />

        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div>
              <Form.Group
                controlId="includeInReport"
                className="mb-0 d-inline-flex align-items-center"
              >
                <Form.Check
                  type="checkbox"
                  id="includeInReport"
                  checked={draft.include_in_report || false}
                  onChange={(e) =>
                    setDraft((prev: any) => ({
                      ...prev,
                      include_in_report: e.target.checked,
                    }))
                  }
                />
                <Form.Label htmlFor="includeInReport" className="ms-2 mb-0">
                  Include in Published Report
                  <OverlayTrigger
                    trigger="hover"
                    placement="right"
                    overlay={
                      <Popover>
                        <PopoverBody>
                          When selected this question will be included in the
                          published report for the assessment along with it's
                          answer. This is typically used for key questions with
                          numeric values that you would like to highlight in
                          report. (Ex. CO₂ Emissions, Energy Consumption, etc.)
                        </PopoverBody>
                      </Popover>
                    }
                  >
                    <i
                      className="bi bi-info-circle-fill text-muted ms-1"
                      style={{ cursor: 'pointer' }}
                    />
                  </OverlayTrigger>
                </Form.Label>
              </Form.Group>
            </div>
          </div>
        </div>

        <GroupManagerPanel
          open={showGroups}
          setOpen={(open) => {
            if (!open) {
              // Save groups when panel closes
              onSaveQuestionGroups(section.id, availableGroups);

              // Ensure draft.group_id is valid
              cleanDraftGroupId(availableGroups);
            }
            setShowGroups(open);
          }}
          availableGroups={availableGroups}
          setAvailableGroups={handleGroupsChange}
          section={section}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={showGroups}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={showGroups}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionEditorModal;
